// iter-35g — BumpStudio: Super Admin configurator for the side-bump tabs.
// Two parallel configs (mobile/desktop) × two edges (left/right). Live mini
// preview pinned at the bottom so admins can see their changes before saving.
//
// Phase 1 ships the configurator + wires the existing 4 bumps. The
// `source_type` picker is intentionally exposed today even though the
// non-legacy options aren't connected yet — the UX shape is real, the
// content router behind it is what future phases will fill in.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CaretUp, CaretDown, CaretLeft, CaretRight, Plus, Trash, ArrowsCounterClockwise,
  DeviceMobile, Monitor, ArrowsHorizontal,
} from "@phosphor-icons/react";
import * as Phosphor from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { invalidateBumpConfig, slotToTopPct, COLOR_SLOT_VAR, SHAPE_CLASS, SHAPE_CLASS_RIGHT } from "@/lib/bumpConfig";
import { ConfirmModal } from "@/components/PromptModals";
import VoiceLeadInLab from "@/components/VoiceLeadInLab";

export default function BumpStudio() {
  const [cfg, setCfg] = useState(null);
  const [choices, setChoices] = useState(null);
  const [viewport, setViewport] = useState("desktop");
  const [busy, setBusy] = useState(false);
  const [expandedBump, setExpandedBump] = useState(null);

  const reload = async () => {
    try {
      const [c, ch] = await Promise.all([
        api.get("/admin/bump-studio"),
        api.get("/admin/bump-studio/choices"),
      ]);
      setCfg(c.data);
      setChoices(ch.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load Bump Studio config");
    }
  };
  useEffect(() => { reload(); }, []);

  const persistAndRefresh = async (req) => {
    setBusy(true);
    try {
      const r = await req();
      setCfg(r.data);
      invalidateBumpConfig();
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const updateBump = (side, bumpId, patch) =>
    persistAndRefresh(() => api.patch(`/admin/bump-studio/${side}/${viewport}/bumps/${bumpId}`, patch));

  const updateMaxSlots = (side, max_slots) =>
    persistAndRefresh(() => api.patch(`/admin/bump-studio/${side}/${viewport}/max-slots`, { max_slots }));

  const addBump = (side) => {
    const bumps = cfg[side][viewport].bumps;
    const max = cfg[side][viewport].max_slots;
    const used = new Set(bumps.map((b) => b.slot));
    let nextSlot = 1;
    while (used.has(nextSlot) && nextSlot <= max) nextSlot++;
    if (nextSlot > max) { toast.error("All slots are full. Raise the slot count first."); return; }
    const id = `bump_${Date.now().toString(36)}`;
    const newBump = {
      id, name: "New bump", icon: "Sparkle", shape: "tab-classic",
      color_slot: "surface", slot: nextSlot,
      width: viewport === "desktop" ? 52 : 44,
      height: viewport === "desktop" ? 132 : 96,
      drawer_width: viewport === "desktop" ? 384 : 320,
      text_size: 10, source_type: "bookmarks",
    };
    persistAndRefresh(() => api.post(`/admin/bump-studio/${side}/${viewport}/bumps`, newBump))
      .then((d) => { if (d) { setExpandedBump(id); toast.success(`Bump added at slot ${nextSlot}`); } });
  };

  const deleteBump = (side, bumpId) => {
    if (!window.confirm("Remove this bump?")) return;
    persistAndRefresh(() => api.delete(`/admin/bump-studio/${side}/${viewport}/bumps/${bumpId}`));
  };

  const moveBump = (side, bumpId, direction) => {
    const bumps = cfg[side][viewport].bumps;
    const max = cfg[side][viewport].max_slots;
    const target = bumps.find((b) => b.id === bumpId);
    if (!target) return;
    const nextSlot = target.slot + (direction === "up" ? -1 : 1);
    if (nextSlot < 1 || nextSlot > max) return;
    if (bumps.some((b) => b.slot === nextSlot && b.id !== bumpId)) {
      toast.error(`Slot ${nextSlot} is occupied. Move that bump first.`);
      return;
    }
    updateBump(side, bumpId, { slot: nextSlot });
  };

  // window.confirm() is blocked inside the hosting preview iframe — it silently
  // returns false, so the OLD code quit early and NOTHING happened (no save, no
  // toast, no persistence). That's why "Save current as default" gave no feedback
  // and "Reset" never worked. Save-as-default is non-destructive, so it just runs
  // + toasts. Reset IS destructive, so it routes through an in-app ConfirmModal.
  const [confirmReset, setConfirmReset] = useState(false);

  const doReset = () => {
    persistAndRefresh(() => api.post("/admin/bump-studio/reset"))
      .then((d) => { if (d) toast.success("Reverted to your last saved default."); });
  };

  const saveAsDefault = () => {
    persistAndRefresh(() => api.post("/admin/bump-studio/save-as-default"))
      .then((d) => { if (d) toast.success("Saved — this layout is now your default restore point."); });
  };

  if (!cfg || !choices) return <div className="text-sm text-[#6B6A68] p-6">Loading Bump Studio…</div>;

  return (
    <div data-testid="bump-studio">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
        <div>
          <h2 className="font-serif-display text-2xl">Bump Studio</h2>
          <p className="text-xs text-[#6B6A68] mt-1 max-w-xl">
            Configure the side-bump tabs that appear on every silo page. Two parallel layouts (mobile + desktop), two edges (left + right). Default behaviors (tap-anywhere-to-close, top-layer-when-open) are baked in and can't be disabled.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={saveAsDefault} disabled={busy} data-testid="bumpstudio-save-as-default"
            title="Snapshot the current layout as the new default. Any future 'Reset to defaults' click will revert to this snapshot."
            className="text-xs px-3 py-2 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5">
            Save current as default
          </button>
          <button onClick={() => setConfirmReset(true)} disabled={busy} data-testid="bumpstudio-reset"
            className="text-xs px-3 py-2 rounded-md border hairline bg-white hover:border-[#C16E5A] hover:text-[#C16E5A] flex items-center gap-1.5">
            <ArrowsCounterClockwise size={12} /> Reset to defaults
          </button>
        </div>
      </div>

      {/* Viewport toggle — controls every section below */}
      <div className="flex items-center gap-2 mb-6 bg-[#FDFCF8] border hairline rounded-lg p-1 w-fit" data-testid="bumpstudio-viewport-toggle">
        {[
          { id: "desktop", label: "Desktop", Icon: Monitor },
          { id: "mobile", label: "Mobile", Icon: DeviceMobile },
        ].map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setViewport(id)} data-testid={`bumpstudio-vp-${id}`}
            className={`px-4 py-2 rounded-md text-xs flex items-center gap-1.5 transition-colors ${
              viewport === id ? "bg-[#1C1B1A] text-[#FDFCF8]" : "text-[#6B6A68] hover:text-[#1C1B1A]"
            }`}>
            <Icon size={14} weight="duotone" /> {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EdgePanel
          side="left"
          edge={cfg.left[viewport]}
          choices={choices}
          viewport={viewport}
          expandedBump={expandedBump}
          setExpandedBump={setExpandedBump}
          onMaxSlotsChange={(n) => updateMaxSlots("left", n)}
          onBumpPatch={(id, p) => updateBump("left", id, p)}
          onAdd={() => addBump("left")}
          onDelete={(id) => deleteBump("left", id)}
          onMove={(id, dir) => moveBump("left", id, dir)}
        />
        <EdgePanel
          side="right"
          edge={cfg.right[viewport]}
          choices={choices}
          viewport={viewport}
          expandedBump={expandedBump}
          setExpandedBump={setExpandedBump}
          onMaxSlotsChange={(n) => updateMaxSlots("right", n)}
          onBumpPatch={(id, p) => updateBump("right", id, p)}
          onAdd={() => addBump("right")}
          onDelete={(id) => deleteBump("right", id)}
          onMove={(id, dir) => moveBump("right", id, dir)}
        />
      </div>

      <PreviewPane cfg={cfg} viewport={viewport} />

      {/* Sprint 2 — Chat surface aesthetics. New singleton-attached config block;
          save-as-default already covers it. */}
      <ChatAestheticsSection cfg={cfg} choices={choices}
        onPatch={(patch) => persistAndRefresh(() => api.patch("/admin/bump-studio/chat-aesthetics", patch))} />

      {/* Feb 19, 2026 — Composer composition. Lives in BumpStudio because
          it's a visual / UX dial the super_admin tunes once for the whole
          platform (per-silo overrides via dragging still apply). Writes
          to /admin/thresholds instead of /admin/bump-studio. */}
      <ComposerCompositionSection />

      {/* Sep 29, 2026 (Randy pt-voice-clip-lab) — premium-voice lead-in dials
          + the count-in instrument that measures how much a device is losing
          before the first word. Lives here because it's a platform-wide
          playback dial, tuned once. */}
      <VoiceLeadInLab />

      <ConfirmModal
        open={confirmReset}
        title="Reset Bump Studio?"
        message="This throws away your CURRENT layout (mobile + desktop, both edges) and restores your last saved default. If you've never saved a default, it restores the platform factory layout."
        confirmLabel="Yes, reset"
        danger
        onConfirm={doReset}
        onClose={() => setConfirmReset(false)}
      />
    </div>
  );
}

// ----- Composer composition (Feb 19, 2026) ------------------------------
//
// Two global dials:
//   1. Default composer textarea height (px). Lives in
//      platform_thresholds.composer_default_height_px. Used when a silo
//      hasn't set its own override.
//   2. Whether the ↕ teaching hint fades in on the divider on first
//      paint. Lives in platform_thresholds.composer_divider_hint_enabled.
//
// Both auto-save on change (matches the rest of BumpStudio's pattern;
// the top "Save current as default" snapshot only covers bump-studio
// config, NOT platform_thresholds).
function ComposerCompositionSection() {
  const [heightPx, setHeightPx] = useState(54);
  const [hintEnabled, setHintEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initial load.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/admin/thresholds");
        if (cancel) return;
        const t = r.data?.thresholds || {};
        setHeightPx(Number(t.composer_default_height_px ?? 54));
        setHintEnabled(t.composer_divider_hint_enabled !== false);
      } catch {/* stay on defaults */}
      finally { if (!cancel) setLoaded(true); }
    })();
    return () => { cancel = true; };
  }, []);

  const patch = async (updates) => {
    setSaving(true);
    try {
      await api.patch("/admin/thresholds", { updates });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const stepHeight = (delta) => {
    const next = Math.max(44, Math.min(600, heightPx + delta));
    setHeightPx(next);
    patch({ composer_default_height_px: next });
  };

  const onTypeHeight = (e) => {
    const v = parseInt(e.target.value || "0", 10);
    if (Number.isNaN(v)) return;
    const next = Math.max(44, Math.min(600, v));
    setHeightPx(next);
    patch({ composer_default_height_px: next });
  };

  const toggleHint = () => {
    const next = !hintEnabled;
    setHintEnabled(next);
    patch({ composer_divider_hint_enabled: next });
  };

  if (!loaded) {
    return (
      <section className="bg-white border hairline rounded-lg p-5 mt-6" data-testid="bumpstudio-composer-composition">
        <div className="text-sm text-[#6B6A68]">Loading composer settings…</div>
      </section>
    );
  }

  return (
    <section className="bg-white border hairline rounded-lg p-5 mt-6" data-testid="bumpstudio-composer-composition">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-serif-display text-lg">Composer composition</h3>
        {saving && <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Saving…</span>}
      </div>
      <p className="text-xs text-[#6B6A68] mb-4 max-w-2xl">
        Global defaults for the public Q&amp;A chat input. Each setting auto-saves on change. Per-silo overrides (creators dragging their own composer) still win when present.
      </p>

      {/* Default composer height stepper */}
      <div className="bg-[#FDFCF8] border hairline rounded-md p-4 mb-3" data-testid="bs-composer-height-row">
        <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.18em] text-[#6B6A68]">Default composer height</div>
            <div className="text-[11px] text-[#6B6A68] mt-0.5">
              Starting height of the chat input when a silo hasn&apos;t saved its own. 44–600px.
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => stepHeight(-1)}
              data-testid="bs-composer-height-minus"
              className="w-8 h-8 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#4A5D4E] flex items-center justify-center text-base"
              aria-label="Decrease default composer height by 1px"
            >
              −
            </button>
            <input
              type="number"
              min={44}
              max={600}
              value={heightPx}
              onChange={onTypeHeight}
              data-testid="bs-composer-height-input"
              className="w-20 text-center bg-white border hairline rounded-md px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            />
            <span className="text-xs text-[#6B6A68] ml-1">px</span>
            <button
              type="button"
              onClick={() => stepHeight(1)}
              data-testid="bs-composer-height-plus"
              className="w-8 h-8 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#4A5D4E] flex items-center justify-center text-base"
              aria-label="Increase default composer height by 1px"
            >
              +
            </button>
          </div>
        </div>
      </div>

      {/* Divider hint toggle */}
      <div className="bg-[#FDFCF8] border hairline rounded-md p-4" data-testid="bs-composer-hint-row">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={hintEnabled}
            onChange={toggleHint}
            data-testid="bs-composer-hint-toggle"
            className="mt-1 w-4 h-4 accent-[#4A5D4E]"
          />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.18em] text-[#1C1B1A] font-medium">Composer divider hint</div>
            <div className="text-[11px] text-[#6B6A68] mt-0.5">
              Briefly fade in a small <strong>↕</strong> indicator on the line above the composer when a visitor first lands on a public silo. Teaches them the line is draggable. Disappears after 3 seconds. Turn off if you find it noisy.
            </div>
          </div>
        </label>
      </div>
    </section>
  );
}

// ----- Edge panel (left or right) ---------------------------------------

function EdgePanel({ side, edge, choices, viewport, expandedBump, setExpandedBump, onMaxSlotsChange, onBumpPatch, onAdd, onDelete, onMove }) {
  const filledSlots = edge.bumps.length;
  const max = edge.max_slots;
  return (
    <section className="bg-white border hairline rounded-lg p-5" data-testid={`bumpstudio-edge-${side}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif-display text-lg">{side === "left" ? "Left edge" : "Right edge"}</h3>
        <span className="text-xs text-[#6B6A68]">{filledSlots} of {max} slots filled</span>
      </div>

      {/* Max slots stepper */}
      <div className="flex items-center gap-2 mb-4 bg-[#FDFCF8] border hairline rounded-md p-3">
        <span className="text-xs uppercase tracking-[0.18em] text-[#6B6A68] flex-1">Max slots on this edge</span>
        <button onClick={() => onMaxSlotsChange(Math.max(2, max - 1))} disabled={max <= filledSlots}
          data-testid={`bumpstudio-${side}-maxslots-down`}
          className="p-1.5 rounded border hairline hover:border-[#4A5D4E] disabled:opacity-30 disabled:cursor-not-allowed">
          <CaretDown size={12} />
        </button>
        <input type="number" min="2" max="20" value={max}
          onChange={(e) => {
            const v = Math.max(2, Math.min(20, parseInt(e.target.value || "0", 10)));
            if (v >= filledSlots) onMaxSlotsChange(v);
          }}
          data-testid={`bumpstudio-${side}-maxslots-input`}
          className="w-12 text-center text-sm tabular-nums bg-white border hairline rounded px-1 py-1" />
        <button onClick={() => onMaxSlotsChange(Math.min(20, max + 1))}
          data-testid={`bumpstudio-${side}-maxslots-up`}
          className="p-1.5 rounded border hairline hover:border-[#4A5D4E]">
          <CaretUp size={12} />
        </button>
      </div>

      {/* Bumps list */}
      <div className="space-y-3">
        {edge.bumps.length === 0 && (
          <div className="text-xs text-[#6B6A68] italic text-center p-4 border hairline border-dashed rounded-md">
            No bumps on this edge yet.
          </div>
        )}
        {[...edge.bumps].sort((a, b) => a.slot - b.slot).map((b) => (
          <BumpRow key={b.id} bump={b} side={side} maxSlots={max}
            choices={choices} viewport={viewport}
            expanded={expandedBump === b.id}
            onToggle={() => setExpandedBump(expandedBump === b.id ? null : b.id)}
            onPatch={(p) => onBumpPatch(b.id, p)}
            onDelete={() => onDelete(b.id)}
            onMove={(dir) => onMove(b.id, dir)}
          />
        ))}
      </div>

      <button onClick={onAdd} disabled={filledSlots >= max}
        data-testid={`bumpstudio-${side}-add`}
        className="mt-4 w-full px-4 py-3 rounded-md border-2 border-dashed hairline text-sm text-[#4A5D4E] hover:border-[#4A5D4E] hover:bg-[#F0F1EE] transition-colors flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed">
        <Plus size={14} /> Add bump
      </button>
    </section>
  );
}

// ----- Single bump row (collapsed + expanded) --------------------------

function BumpRow({ bump, side, maxSlots, choices, viewport, expanded, onToggle, onPatch, onDelete, onMove }) {
  const [draftName, setDraftName] = useState(bump.name || "");
  useEffect(() => setDraftName(bump.name || ""), [bump.name]);

  const showIcon = bump.icon !== "None";
  const PhosIcon = Phosphor[bump.icon] || Phosphor.Sparkle;
  const previewLabel = bump.name || (showIcon ? "" : "(blank tab)");

  return (
    <div className="border hairline rounded-md bg-[#FDFCF8]" data-testid={`bumpstudio-${side}-bump-${bump.id}`}>
      {/* Collapsed header — always visible */}
      <div className="flex items-center gap-2 p-3">
        <span className="font-mono text-[10px] text-[#6B6A68] tabular-nums w-6 text-center">#{bump.slot}</span>
        {showIcon ? <PhosIcon size={14} weight="duotone" className="text-[#4A5D4E]" /> : <span className="text-[10px] text-[#6B6A68] w-3.5 text-center italic">—</span>}
        <button onClick={onToggle} className={`flex-1 text-left text-sm font-medium truncate hover:underline ${bump.name ? "text-[#1C1B1A]" : "text-[#6B6A68] italic"}`}
          data-testid={`bumpstudio-bump-toggle-${bump.id}`}>
          {previewLabel || "(no name)"}
        </button>
        <span className="text-[9px] uppercase tracking-[0.18em] text-[#6B6A68]">{bump.shape.replace("tab-", "")}</span>
        <button onClick={() => onMove("up")} disabled={bump.slot <= 1}
          data-testid={`bumpstudio-bump-up-${bump.id}`}
          className="p-1 rounded hover:bg-[#F0F1EE] disabled:opacity-20 disabled:cursor-not-allowed"><CaretUp size={12} /></button>
        <button onClick={() => onMove("down")} disabled={bump.slot >= maxSlots}
          data-testid={`bumpstudio-bump-down-${bump.id}`}
          className="p-1 rounded hover:bg-[#F0F1EE] disabled:opacity-20 disabled:cursor-not-allowed"><CaretDown size={12} /></button>
        <button onClick={onDelete}
          data-testid={`bumpstudio-bump-delete-${bump.id}`}
          className="p-1 rounded hover:bg-[#F0F1EE] hover:text-[#C16E5A]"><Trash size={12} /></button>
      </div>

      {/* Expanded editor */}
      {expanded && (
        <div className="border-t hairline p-3 space-y-3 bg-white" data-testid={`bumpstudio-bump-editor-${bump.id}`}>
          <Field label="Name" hint="Leave blank for icon-only tab.">
            <input type="text" value={draftName} maxLength={40}
              placeholder="(blank = no name)"
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={() => { if (draftName !== (bump.name || "")) onPatch({ name: draftName.trim() }); }}
              data-testid={`bumpstudio-bump-name-${bump.id}`}
              className="w-full text-sm border hairline rounded px-2 py-1.5 bg-white" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Slot position">
              <NumStepper value={bump.slot} min={1} max={maxSlots} step={0.01} precision={2}
                onChange={(v) => onPatch({ slot: v })}
                testid={`bumpstudio-bump-slot-${bump.id}`} />
            </Field>
            <Field label="Text size (px)">
              <NumStepper value={bump.text_size} min={8} max={18}
                onChange={(v) => onPatch({ text_size: v })}
                testid={`bumpstudio-bump-textsize-${bump.id}`} />
            </Field>
            <Field label="Width (px)">
              <NumStepper value={bump.width} min={24} max={120} step={2}
                onChange={(v) => onPatch({ width: v })}
                testid={`bumpstudio-bump-width-${bump.id}`} />
            </Field>
            <Field label="Height (px)">
              <NumStepper value={bump.height} min={48} max={240} step={4}
                onChange={(v) => onPatch({ height: v })}
                testid={`bumpstudio-bump-height-${bump.id}`} />
            </Field>
            <Field label="Drawer width (px)" wide>
              <NumStepper value={bump.drawer_width} min={240} max={600} step={8}
                onChange={(v) => onPatch({ drawer_width: v })}
                testid={`bumpstudio-bump-drawer-${bump.id}`} />
            </Field>
            {/* Sprint 3 — drawer top/bottom inset (vh%). Lets admins shrink the
                drawer's vertical bleed from full-height to a centered card. */}
            <Field label="Drawer top inset (% of viewport height)">
              <NumStepper value={bump.drawer_top_pct ?? 0} min={0} max={40} step={1}
                onChange={(v) => onPatch({ drawer_top_pct: v })}
                testid={`bumpstudio-bump-drawer-top-${bump.id}`} />
            </Field>
            <Field label="Drawer bottom inset (% of viewport height)">
              <NumStepper value={bump.drawer_bottom_pct ?? 0} min={0} max={40} step={1}
                onChange={(v) => onPatch({ drawer_bottom_pct: v })}
                testid={`bumpstudio-bump-drawer-bottom-${bump.id}`} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Shape">
              <select value={bump.shape} onChange={(e) => onPatch({ shape: e.target.value })}
                data-testid={`bumpstudio-bump-shape-${bump.id}`}
                className="w-full text-sm border hairline rounded px-2 py-1.5 bg-white">
                {choices.shapes.map((s) => <option key={s} value={s}>{s.replace("tab-", "")}</option>)}
              </select>
            </Field>
            <Field label="Drawer style">
              <select value={bump.drawer_style || "slide-rect"}
                onChange={(e) => onPatch({ drawer_style: e.target.value })}
                data-testid={`bumpstudio-bump-drawer-style-${bump.id}`}
                className="w-full text-sm border hairline rounded px-2 py-1.5 bg-white">
                {(choices.drawer_styles || ["slide-rect"]).map((s) => (
                  <option key={s} value={s}>{s.replace("slide-", "")}</option>
                ))}
              </select>
            </Field>
            <Field label="Color slot">
              <select value={bump.color_slot} onChange={(e) => onPatch({ color_slot: e.target.value })}
                data-testid={`bumpstudio-bump-color-${bump.id}`}
                className="w-full text-sm border hairline rounded px-2 py-1.5 bg-white">
                {choices.color_slots.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Icon">
              <select value={bump.icon} onChange={(e) => onPatch({ icon: e.target.value })}
                data-testid={`bumpstudio-bump-icon-${bump.id}`}
                className="w-full text-sm border hairline rounded px-2 py-1.5 bg-white">
                {choices.icons.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </Field>
            <Field label="Source · drawer content">
              <select value={bump.source_type} onChange={(e) => onPatch({ source_type: e.target.value })}
                data-testid={`bumpstudio-bump-source-${bump.id}`}
                className="w-full text-sm border hairline rounded px-2 py-1.5 bg-white">
                {choices.source_types.map((s) => (
                  <option key={s} value={s}>
                    {LEGACY_SOURCES.has(s) ? s : `${s} · coming soon`}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

const LEGACY_SOURCES = new Set(["bookmarks", "tools", "discover", "invite"]);

function Field({ label, children, wide, hint }) {
  return (
    <label className={`flex flex-col gap-1 ${wide ? "col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-[#6B6A68] italic">{hint}</span>}
    </label>
  );
}

function NumStepper({ value, min, max, step = 1, precision = 0, onChange, testid }) {
  // Feb 14, 2026 — `precision` enables fractional steps (e.g. 0.01 for the
  // per-bump Height field). When precision === 0 we keep int-only behavior.
  const round = (n) => {
    if (precision <= 0) return Math.round(n);
    const f = Math.pow(10, precision);
    return Math.round(n * f) / f;
  };
  const fmt = (v) => (precision > 0 ? Number(v).toFixed(precision) : String(v));

  // Jun 1, 2026 — Local draft string so you can click in, CLEAR the box, and
  // type whatever you want freely. We only clamp + save on blur or Enter — NOT
  // on every keystroke (the old code re-clamped + re-saved each keypress, which
  // made the field jump and refuse partial/empty values).
  const [draft, setDraft] = useState(fmt(value));
  useEffect(() => { setDraft(fmt(value)); }, [value, precision]); // re-sync if value changes elsewhere

  const commit = () => {
    const raw = String(draft).trim();
    if (raw === "" || raw === "-" || raw === ".") { setDraft(fmt(value)); return; }
    const v = precision > 0 ? parseFloat(raw) : parseInt(raw, 10);
    if (Number.isNaN(v)) { setDraft(fmt(value)); return; }
    const clamped = round(Math.max(min, Math.min(max, v)));
    setDraft(fmt(clamped));
    if (clamped !== value) onChange(clamped);
  };

  const dec = () => onChange(round(Math.max(min, value - step)));
  const inc = () => onChange(round(Math.min(max, value + step)));

  return (
    <div className="flex items-center gap-1.5">
      <button onClick={dec} disabled={value <= min}
        data-testid={`${testid}-down`}
        className="p-1 rounded border hairline hover:border-[#4A5D4E] disabled:opacity-30 disabled:cursor-not-allowed">
        <CaretLeft size={12} />
      </button>
      <input type="number" value={draft} min={min} max={max} step={step}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
        data-testid={testid}
        className="flex-1 text-sm text-center tabular-nums border hairline rounded px-1 py-1 bg-white" />
      <button onClick={inc} disabled={value >= max}
        data-testid={`${testid}-up`}
        className="p-1 rounded border hairline hover:border-[#4A5D4E] disabled:opacity-30 disabled:cursor-not-allowed">
        <CaretRight size={12} />
      </button>
    </div>
  );
}

// ----- Live preview (mini silo viewport with bumps in their actual positions)

function PreviewPane({ cfg, viewport }) {
  // Render a 16:9 stage at ~640px wide on desktop, 320px on mobile.
  const stageW = viewport === "desktop" ? 640 : 320;
  const stageH = viewport === "desktop" ? 360 : 480;

  const renderBumps = (side, bumpsCfg) => {
    return bumpsCfg.bumps.map((b) => {
      const PhosIcon = Phosphor[b.icon] || Phosphor.Sparkle;
      const top = slotToTopPct(b.slot, bumpsCfg.max_slots);
      // Scale tabs down to fit the preview stage (real values are too big)
      const scale = viewport === "desktop" ? 0.6 : 0.5;
      const w = Math.round(b.width * scale);
      const h = Math.round(b.height * scale);
      const colors = COLOR_SLOT_VAR[b.color_slot] || COLOR_SLOT_VAR.surface;
      const shapeMap = side === "left" ? SHAPE_CLASS : SHAPE_CLASS_RIGHT;
      const shapeClass = shapeMap[b.shape] || shapeMap["tab-classic"];
      return (
        <div key={b.id}
          data-testid={`bumpstudio-preview-${side}-${b.id}`}
          className={`absolute flex flex-col items-center justify-center gap-1 border ${shapeClass}`}
          style={{
            [side]: 0,
            top, transform: `translateY(-50%)`,
            width: `${w}px`, height: `${h}px`,
            background: colors.bg, color: colors.fg,
            borderColor: "rgba(0,0,0,0.08)",
          }}>
          {b.icon !== "None" && <PhosIcon size={Math.max(10, Math.round(b.text_size * 1.1))} weight="duotone" />}
          {b.name && (
            <span style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: `${Math.round(b.text_size * scale * 1.4)}px` }}
              className="uppercase tracking-[0.18em] font-medium whitespace-nowrap overflow-hidden">
              {b.name}
            </span>
          )}
        </div>
      );
    });
  };

  return (
    <section className="mt-8" data-testid="bumpstudio-preview">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-serif-display text-lg">Live preview</h3>
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">{viewport}</span>
      </div>
      <div className="flex justify-center bg-[#FDFCF8] border hairline rounded-lg p-6">
        <div
          className="relative bg-white border hairline rounded-lg overflow-hidden"
          style={{ width: `${stageW}px`, height: `${stageH}px` }}
        >
          {/* Faux silo header */}
          <div className="absolute top-0 left-0 right-0 h-10 border-b hairline bg-[#FDFCF8] flex items-center px-4 text-xs text-[#6B6A68]">
            Faux silo viewport · {viewport}
          </div>
          {/* Faux content */}
          <div className="absolute inset-0 flex items-center justify-center text-[10px] text-[#6B6A68] uppercase tracking-[0.2em] pointer-events-none">
            silo content
          </div>
          {renderBumps("left", cfg.left[viewport])}
          {renderBumps("right", cfg.right[viewport])}
        </div>
      </div>
      <p className="text-[10px] text-[#6B6A68] text-center mt-2">
        Bumps shown at {viewport === "desktop" ? "60" : "50"}% scale to fit the preview frame. The live site renders at 100%.
      </p>
    </section>
  );
}


// iter-39 Sprint 2 — Chat surface aesthetics editor
function ChatAestheticsSection({ cfg, choices, onPatch }) {
  const ca = cfg.chat_aesthetics || {};
  const styles = choices.tip_badge_styles || ["pill", "pill-outlined", "flat-bar", "floating-card", "inline-link", "chip"];

  return (
    <section className="mt-8 bg-white border hairline rounded-lg p-5" data-testid="bumpstudio-chat-aesthetics">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="font-serif-display text-lg">Chat surface</h3>
          <p className="text-xs text-[#6B6A68] mt-1 max-w-xl">
            Aesthetics for the public Q&amp;A chat — tip badge style and color.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Tip badge style */}
        <label className="flex flex-col gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Tip badge style</span>
          <select value={ca.tip_badge_style || "pill"}
            onChange={(e) => onPatch({ tip_badge_style: e.target.value })}
            data-testid="chat-aesthetics-tip-style"
            className="px-2 py-1.5 rounded border hairline text-sm bg-white">
            {styles.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>

        {/* Tip badge color */}
        <label className="flex flex-col gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Tip badge color</span>
          <div className="flex items-center gap-2">
            <input type="color" value={ca.tip_badge_color || "#6B6A68"}
              onChange={(e) => onPatch({ tip_badge_color: e.target.value })}
              data-testid="chat-aesthetics-tip-color"
              className="w-12 h-8 rounded border hairline bg-white cursor-pointer" />
            <span className="font-mono text-xs text-[#6B6A68] tabular-nums">{ca.tip_badge_color || "#6B6A68"}</span>
            <button onClick={() => onPatch({ tip_badge_color: "#6B6A68" })}
              data-testid="chat-aesthetics-tip-color-reset-gray"
              className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded border hairline text-[#6B6A68] hover:text-[#1C1B1A] hover:border-[#1C1B1A] ml-auto">
              Reset gray
            </button>
          </div>
        </label>

        {/* Tip badge text color */}
        <label className="flex flex-col gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Tip badge text color</span>
          <div className="flex items-center gap-2">
            <input type="color" value={ca.tip_badge_text_color || "#FFFFFF"}
              onChange={(e) => onPatch({ tip_badge_text_color: e.target.value })}
              data-testid="chat-aesthetics-tip-text-color"
              className="w-12 h-8 rounded border hairline bg-white cursor-pointer" />
            <span className="font-mono text-xs text-[#6B6A68] tabular-nums">{ca.tip_badge_text_color || "#FFFFFF"}</span>
          </div>
        </label>
      </div>

      {/* Live preview */}
      <div className="mt-4 p-4 bg-[#FDFCF8] border hairline rounded-md">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-2">Tip badge preview</div>
        <ChatTipBadgePreview ca={ca} />
      </div>
    </section>
  );
}

function ChatTipBadgePreview({ ca }) {
  const style = ca.tip_badge_style || "pill";
  const bg = ca.tip_badge_color || "#6B6A68";
  const fg = ca.tip_badge_text_color || "#FFFFFF";
  const baseTxt = "💛 Tip Cosmic Thought";

  switch (style) {
    case "pill":
      return <span data-testid="tip-badge-preview" className="text-xs px-3 py-1.5 rounded-full" style={{ background: bg, color: fg }}>{baseTxt}</span>;
    case "pill-outlined":
      return <span data-testid="tip-badge-preview" className="text-xs px-3 py-1.5 rounded-full border-2" style={{ borderColor: bg, color: bg, background: "transparent" }}>{baseTxt}</span>;
    case "flat-bar":
      return <span data-testid="tip-badge-preview" className="text-xs px-4 py-2 rounded-none uppercase tracking-[0.18em]" style={{ background: bg, color: fg }}>{baseTxt}</span>;
    case "floating-card":
      return <span data-testid="tip-badge-preview" className="text-xs px-3 py-2 rounded-lg shadow-lg" style={{ background: bg, color: fg }}>{baseTxt}</span>;
    case "inline-link":
      return <span data-testid="tip-badge-preview" className="text-xs underline" style={{ color: bg, background: "transparent" }}>{baseTxt}</span>;
    case "chip":
      return <span data-testid="tip-badge-preview" className="text-xs px-2.5 py-1 rounded-md font-medium" style={{ background: bg, color: fg }}>{baseTxt}</span>;
    default:
      return <span data-testid="tip-badge-preview">{baseTxt}</span>;
  }
}
